import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type EventRow = {
  event_type: "impression" | "click";
  source: "pdp_fbt" | "cart" | string;
  session_id: string;
  recommended_product_id: string;
  created_at: string;
  metadata?: { action?: string; bundle_ids?: string[] } | null;
};

type SurfaceAgg = { surface: string; impressions: number; clicks: number; ctr: number };
type SessionAgg = { session_id: string; impressions: number; clicks: number; ctr: number };
type ProductAgg = { key: string; label: string; isBundle: boolean; impressions: number; clicks: number; ctr: number };

const RANGES = [
  { id: "24h", label: "Last 24h", ms: 24 * 60 * 60 * 1000 },
  { id: "7d", label: "Last 7 days", ms: 7 * 24 * 60 * 60 * 1000 },
  { id: "30d", label: "Last 30 days", ms: 30 * 24 * 60 * 60 * 1000 },
  { id: "all", label: "All time", ms: 0 },
];

export function CrossSellDashboard({ lang = "en" }: { lang?: string }) {
  const [rows, setRows] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rangeId, setRangeId] = useState("7d");
  const [productNames, setProductNames] = useState<Record<string, string>>({});
  const [productSort, setProductSort] = useState<{ key: "ctr" | "clicks" | "impressions"; desc: boolean }>({ key: "clicks", desc: true });
  const [productQuery, setProductQuery] = useState("");
  const [productTypeFilter, setProductTypeFilter] = useState<"all" | "product" | "bundle">("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      const range = RANGES.find(r => r.id === rangeId)!;
      let q = supabase
        .from("cross_sell_events")
        .select("event_type, source, session_id, recommended_product_id, created_at, metadata")
        .order("created_at", { ascending: false })
        .limit(10000);
      if (range.ms > 0) {
        q = q.gte("created_at", new Date(Date.now() - range.ms).toISOString());
      }
      const { data, error } = await q;
      if (cancelled) return;
      if (error) setError(error.message);
      else setRows((data as EventRow[]) || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [rangeId]);

  // Load product names for ids appearing in events
  useEffect(() => {
    const ids = new Set<string>();
    for (const r of rows) {
      if (r.recommended_product_id) ids.add(r.recommended_product_id);
      const bIds = r.metadata?.bundle_ids;
      if (Array.isArray(bIds)) bIds.forEach(id => id && ids.add(id));
    }
    const missing = Array.from(ids).filter(id => !(id in productNames) && /^[0-9a-f-]{36}$/i.test(id));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase.from("products").select("id, name").in("id", missing);
      if (cancelled || !data) return;
      setProductNames(prev => {
        const next = { ...prev };
        for (const p of data as { id: string; name: string }[]) next[p.id] = p.name;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [rows, productNames]);

  const { totals, bySurface, bySession, byProduct, byProductFiltered, maxProductCtr } = useMemo(() => {
    const surfaceMap = new Map<string, { i: number; c: number }>();
    const sessionMap = new Map<string, { i: number; c: number }>();
    const productMap = new Map<string, { isBundle: boolean; i: number; c: number }>();
    let impTotal = 0, clkTotal = 0;
    for (const r of rows) {
      const isImp = r.event_type === "impression";
      const isClk = r.event_type === "click";
      if (isImp) impTotal++;
      if (isClk) clkTotal++;
      const s = surfaceMap.get(r.source) || { i: 0, c: 0 };
      if (isImp) s.i++; if (isClk) s.c++;
      surfaceMap.set(r.source, s);
      const ss = sessionMap.get(r.session_id) || { i: 0, c: 0 };
      if (isImp) ss.i++; if (isClk) ss.c++;
      sessionMap.set(r.session_id, ss);

      // Aggregate by recommended product / bundle
      const isBundle = r.metadata?.action === "bundle_all" || Array.isArray(r.metadata?.bundle_ids);
      if (isBundle) {
        const bIds = (r.metadata?.bundle_ids || []).slice().sort();
        const key = `bundle:${bIds.join("+") || r.recommended_product_id}`;
        const p = productMap.get(key) || { isBundle: true, i: 0, c: 0 };
        if (isImp) p.i++; if (isClk) p.c++;
        productMap.set(key, p);
      } else if (r.recommended_product_id) {
        const key = `product:${r.recommended_product_id}`;
        const p = productMap.get(key) || { isBundle: false, i: 0, c: 0 };
        if (isImp) p.i++; if (isClk) p.c++;
        productMap.set(key, p);
      }
    }
    const bySurface: SurfaceAgg[] = Array.from(surfaceMap.entries())
      .map(([surface, v]) => ({ surface, impressions: v.i, clicks: v.c, ctr: v.i ? (v.c / v.i) * 100 : 0 }))
      .sort((a, b) => b.impressions - a.impressions);
    const bySession: SessionAgg[] = Array.from(sessionMap.entries())
      .map(([session_id, v]) => ({ session_id, impressions: v.i, clicks: v.c, ctr: v.i ? (v.c / v.i) * 100 : 0 }))
      .sort((a, b) => b.clicks - a.clicks || b.impressions - a.impressions)
      .slice(0, 50);
    const labelFor = (key: string, isBundle: boolean) => {
      if (isBundle) {
        const ids = key.replace(/^bundle:/, "").split("+").filter(Boolean);
        const names = ids.map(id => productNames[id] || id.slice(0, 8));
        return `Bundle · ${names.join(" + ")}`;
      }
      const id = key.replace(/^product:/, "");
      return productNames[id] || id;
    };
    const byProductRaw: ProductAgg[] = Array.from(productMap.entries())
      .map(([key, v]) => ({ key, label: labelFor(key, v.isBundle), isBundle: v.isBundle,
        impressions: v.i, clicks: v.c, ctr: v.i ? (v.c / v.i) * 100 : 0 }));
    const q = productQuery.trim().toLowerCase();
    const filteredRaw = byProductRaw.filter(p => {
      if (productTypeFilter !== "all" && (productTypeFilter === "bundle") !== p.isBundle) return false;
      if (!q) return true;
      return p.label.toLowerCase().includes(q);
    });
    const byProductFiltered = filteredRaw
      .sort((a, b) => {
        const k = productSort.key;
        const diff = (b[k] as number) - (a[k] as number);
        return productSort.desc ? diff : -diff;
      })
      .slice(0, 50);
    const byProduct = byProductRaw
      .sort((a, b) => {
        const k = productSort.key;
        const diff = (b[k] as number) - (a[k] as number);
        return productSort.desc ? diff : -diff;
      })
      .slice(0, 50);
    const maxProductCtr = byProductFiltered.length ? Math.max(...byProductFiltered.map(p => p.ctr)) : 0;
    return {
      totals: { impressions: impTotal, clicks: clkTotal, ctr: impTotal ? (clkTotal / impTotal) * 100 : 0, sessions: sessionMap.size },
      bySurface,
      bySession,
      byProduct,
      byProductFiltered,
      maxProductCtr,
    };
  }, [rows, productNames, productSort, productQuery, productTypeFilter]);

  const exportCSV = () => {
    const headers = ["scope", "key", "impressions", "clicks", "ctr_pct"];
    const lines: string[][] = [headers];
    bySurface.forEach(s => lines.push(["surface", s.surface, String(s.impressions), String(s.clicks), s.ctr.toFixed(2)]));
    byProduct.forEach(p => lines.push([p.isBundle ? "bundle" : "product", `"${p.label.replace(/"/g, '""')}"`, String(p.impressions), String(p.clicks), p.ctr.toFixed(2)]));
    bySession.forEach(s => lines.push(["session", s.session_id, String(s.impressions), String(s.clicks), s.ctr.toFixed(2)]));
    const csv = lines.map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `cross-sell-${rangeId}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const T = lang === "es"
    ? { title: "Cross-sell · Analítica", desc: "Impresiones, clics y conversión por superficie y sesión.",
        imp: "Impresiones", clk: "Clics add-to-cart", ctr: "Conversión", convRate: "Tasa de conversión", sess: "Sesiones únicas",
        bySurfaceH: "Por superficie", bySessionH: "Top sesiones por clics", byProductH: "Top productos / bundles recomendados",
        surface: "Superficie", session: "Session ID", product: "Producto / Bundle", type: "Tipo",
        export: "Exportar CSV", empty: "Sin datos en este rango.", loading: "Cargando…",
        search: "Buscar productos o bundles…", all: "Todos", productOnly: "Solo productos", bundleOnly: "Solo bundles" }
    : { title: "Cross-sell · Analytics", desc: "Impressions, add-to-cart clicks, and conversion by surface and session.",
        imp: "Impressions", clk: "Add-to-cart clicks", ctr: "Conversion", convRate: "Conv. rate", sess: "Unique sessions",
        bySurfaceH: "By surface", bySessionH: "Top sessions by clicks", byProductH: "Top recommended products / bundles",
        surface: "Surface", session: "Session ID", product: "Product / Bundle", type: "Type",
        export: "Export CSV", empty: "No data in this range.", loading: "Loading…",
        search: "Search products or bundles…", all: "All", productOnly: "Products only", bundleOnly: "Bundles only" };

  const surfaceLabel = (s: string) => s === "pdp_fbt" ? "PDP · Frequently bought together" : s === "cart" ? "Cart drawer" : s;

  const sortHeader = (label: string, key: "ctr" | "clicks" | "impressions") => (
    <button
      onClick={() => setProductSort(prev => ({ key, desc: prev.key === key ? !prev.desc : true }))}
      style={{ background: "none", border: "none", padding: 0, color: "inherit", font: "inherit", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4 }}
    >
      {label}
      <span style={{ opacity: productSort.key === key ? 1 : 0.3, fontSize: 10 }}>
        {productSort.key === key ? (productSort.desc ? "▼" : "▲") : "▼"}
      </span>
    </button>
  );

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <h3 style={{ margin: 0 }}>{T.title}</h3>
          <p style={{ margin: "4px 0 0", color: "var(--tx3)", fontSize: 13 }}>{T.desc}</p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={rangeId} onChange={e => setRangeId(e.target.value)}
            style={{ padding: "6px 10px", background: "var(--bg2)", color: "var(--tx)", border: "1px solid var(--bd2)", borderRadius: 8 }}>
            {RANGES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
          <button onClick={exportCSV} className="btn" style={{ padding: "6px 12px" }}>{T.export}</button>
        </div>
      </div>

      {error && <div style={{ padding: 10, background: "rgba(239,68,68,.1)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 8, color: "#fca5a5" }}>{error}</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12 }}>
        <KpiCard label={T.imp} value={totals.impressions.toLocaleString()} />
        <KpiCard label={T.clk} value={totals.clicks.toLocaleString()} />
        <KpiCard label={T.ctr} value={`${totals.ctr.toFixed(2)}%`} accent />
        <KpiCard label={T.sess} value={totals.sessions.toLocaleString()} />
      </div>

      <Section title={T.bySurfaceH}>
        {loading ? <Empty text={T.loading} /> : bySurface.length === 0 ? <Empty text={T.empty} /> : (
          <Table head={[T.surface, T.imp, T.clk, T.ctr]}>
            {bySurface.map(s => (
              <tr key={s.surface}>
                <td>{surfaceLabel(s.surface)}</td>
                <td>{s.impressions.toLocaleString()}</td>
                <td>{s.clicks.toLocaleString()}</td>
                <td><strong>{s.ctr.toFixed(2)}%</strong></td>
              </tr>
            ))}
          </Table>
        )}
      </Section>

      <Section title={T.byProductH}>
        {loading ? <Empty text={T.loading} /> : byProduct.length === 0 ? <Empty text={T.empty} /> : (
          <>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 12, alignItems: "center" }}>
              <input
                type="text"
                value={productQuery}
                onChange={e => setProductQuery(e.target.value)}
                placeholder={T.search}
                style={{ flex: 1, minWidth: 180, padding: "6px 10px", background: "var(--bg)", color: "var(--tx)", border: "1px solid var(--bd2)", borderRadius: 8, fontSize: 13 }}
              />
              <select
                value={productTypeFilter}
                onChange={e => setProductTypeFilter(e.target.value as any)}
                style={{ padding: "6px 10px", background: "var(--bg2)", color: "var(--tx)", border: "1px solid var(--bd2)", borderRadius: 8, fontSize: 13 }}
              >
                <option value="all">{T.all}</option>
                <option value="product">{T.productOnly}</option>
                <option value="bundle">{T.bundleOnly}</option>
              </select>
            </div>
            {byProductFiltered.length === 0 ? <Empty text={T.empty} /> : (
              <Table head={[T.product, T.type, sortHeader(T.imp, "impressions"), sortHeader(T.clk, "clicks"), sortHeader(T.convRate, "ctr")]}>
                {byProductFiltered.map(p => {
                  const barPct = maxProductCtr ? (p.ctr / maxProductCtr) * 100 : 0;
                  return (
                    <tr key={p.key}>
                      <td style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.label}</td>
                      <td>
                        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: p.isBundle ? "rgba(168,85,247,.15)" : "rgba(59,130,246,.15)", color: p.isBundle ? "#c4b5fd" : "#93c5fd" }}>
                          {p.isBundle ? "Bundle" : "Product"}
                        </span>
                      </td>
                      <td>{p.impressions.toLocaleString()}</td>
                      <td>{p.clicks.toLocaleString()}</td>
                      <td style={{ minWidth: 140 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <strong style={{ color: "var(--ac)", minWidth: 48, textAlign: "right" }}>{p.ctr.toFixed(2)}%</strong>
                          <div style={{ flex: 1, height: 6, background: "var(--bg)", borderRadius: 3, overflow: "hidden" }}>
                            <div style={{ width: `${barPct}%`, height: "100%", background: "var(--ac)", borderRadius: 3 }} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </Table>
            )}
          </>
        )}
      </Section>

      <Section title={T.bySessionH}>
        {loading ? <Empty text={T.loading} /> : bySession.length === 0 ? <Empty text={T.empty} /> : (
          <Table head={[T.session, T.imp, T.clk, T.ctr]}>
            {bySession.map(s => (
              <tr key={s.session_id}>
                <td style={{ fontFamily: "monospace", fontSize: 12 }}>{s.session_id.slice(0, 20)}…</td>
                <td>{s.impressions}</td>
                <td>{s.clicks}</td>
                <td>{s.ctr.toFixed(2)}%</td>
              </tr>
            ))}
          </Table>
        )}
      </Section>
    </div>
  );
}

function KpiCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ padding: 14, background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: 10 }}>
      <div style={{ fontSize: 12, color: "var(--tx3)", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent ? "var(--ac)" : "var(--tx)" }}>{value}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: "var(--bg2)", border: "1px solid var(--bd2)", borderRadius: 10, padding: 14 }}>
      <h4 style={{ margin: "0 0 10px" }}>{title}</h4>
      {children}
    </div>
  );
}

function Table({ head, children }: { head: React.ReactNode[]; children: React.ReactNode }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "var(--tx3)", borderBottom: "1px solid var(--bd2)" }}>
            {head.map((h, i) => <th key={i} style={{ padding: "8px 10px", fontWeight: 500 }}>{h}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
        <style>{`tbody tr { border-bottom: 1px solid var(--bd2); } tbody td { padding: 8px 10px; }`}</style>
      </table>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <div style={{ padding: 20, textAlign: "center", color: "var(--tx3)", fontSize: 13 }}>{text}</div>;
}
